import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/it";
import {
  EditionWithSessions,
  ModalEditionChangeProps,
} from "./ModalEditionChange.types";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { getModalEditionChangeStyles } from "./ModalEditionChange.style";
import { Box, Modal, RadioGroup, Typography } from "@mui/material";
import ButtonCst from "@components/ButtonCst";
import { EditionCard } from "./EditionCard.component";

export function ModalEditionChange({
  open,
  handleClose,
  enrollableEditionsWithSessions,
  onEditionChange,
  isVirtual,
  isLoading,
  isPollingEditionChange,
}: ModalEditionChangeProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["modal"]);
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(
    null,
  );
  const [expandedEditionId, setExpandedEditionId] = useState<number | null>(
    null,
  );

  const styles = getModalEditionChangeStyles(theme);

  dayjs.locale(i18n.language);

  const handleEnroll = () => {
    if (selectedEditionId !== null) {
      onEditionChange(selectedEditionId);
    }
  };

  const toggleAccordion = (editionId: number) => {
    setExpandedEditionId(expandedEditionId === editionId ? null : editionId);
  };

  const canEnroll = (editionWithSessions: EditionWithSessions): boolean => {
    return editionWithSessions.remainingPlaces > 0;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-edition-change-title"
      aria-describedby="modal-edition-change-description"
      sx={styles.modalEditionChange.modal}
    >
      <Box sx={styles.modalEditionChange.modalBox}>
        <Box>
          <Box sx={styles.modalEditionChange.headerBox}>
            <Typography
              id="modal-edition-change-title"
              variant="h5"
              sx={styles.modalEditionChange.headerTitle}
            >
              {t("change-edition.title")}
            </Typography>
          </Box>

          <Box sx={styles.modalEditionChange.contentBox}>
            <RadioGroup
              value={selectedEditionId}
              onChange={(e) => setSelectedEditionId(Number(e.target.value))}
            >
              {enrollableEditionsWithSessions
                ?.sort(
                  (a, b) =>
                    new Date(a.startTime).getTime() -
                    new Date(b.startTime).getTime(),
                )
                .map((editionWithSessions) => (
                  <EditionCard
                    key={editionWithSessions.id}
                    editionWithSessions={editionWithSessions}
                    isVirtual={!!isVirtual}
                    disabled={!canEnroll(editionWithSessions)}
                    isExpanded={expandedEditionId === editionWithSessions.id}
                    onToggleAccordion={() =>
                      toggleAccordion(editionWithSessions.id)
                    }
                    theme={theme}
                    t={t}
                  />
                ))}
            </RadioGroup>
          </Box>
        </Box>

        <Box sx={styles.modalEditionChange.footerBox}>
          <ButtonCst
            id="modal-edition-change-cancel"
            variant="outlined"
            onClick={handleClose}
            sx={styles.modalEditionChange.cancelButton}
            text={t("cancel")}
          />
          <ButtonCst
            id="modal-edition-change-enroll"
            disabled={
              selectedEditionId === null || isLoading || isPollingEditionChange
            }
            onClick={handleEnroll}
            sx={styles.modalEditionChange.enrollButton}
            text={t("change-edition.reschedule_action")}
          />
        </Box>
      </Box>
    </Modal>
  );
}
